import _cloneDeep from 'lodash/cloneDeep';
import _get from 'lodash/get';
import _isNil from 'lodash/isNil';
import _isEmpty from 'lodash/isEmpty';
import { Dashboard, Guid, DashboardType, DashboardConfig } from '../types';
import { useReducer } from 'react';

const emptyArray: any[] = [];
const emptyObj: any = {};

export const CONFIGURATION_SET_CACHED_LAYOUTS =
  'CONFIGURATION_SET_CACHED_LAYOUTS';
export const CONFIGURATION_CLEAR_CACHED_LAYOUTS =
  'CONFIGURATION_CLEAR_CACHED_LAYOUTS';
export const CONFIGURATION_UPDATE_SELECTED_LAYOUT =
  'CONFIGURATION_UPDATE_SELECTED_LAYOUT';
export const CONFIGURATION_REVERT_SELECTED_LAYOUT_CHANGES =
  'CONFIGURATION_REVERT_SELECTED_LAYOUT_CHANGES';
export const CONFIGURATION_TOGGLE_RAW_LAYOUT_CONFIG_DIALOG =
  'CONFIGURATION_TOGGLE_RAW_LAYOUT_CONFIG_DIALOG';
export const CONFIGURATION_SELECTED_LAYOUT_SUCCESSFULLY_PERSISTED =
  'CONFIGURATION_SELECTED_LAYOUT_SUCCESSFULLY_PERSISTED';
export const CONFIGURATION_SWITCH_LAYOUT = 'CONFIGURATION_SWITCH_LAYOUT';
export const CONFIGURATION_SWITCH_TO_SPLIT_SCREEN_LAYOUTS =
  'CONFIGURATION_SWITCH_TO_SPLIT_SCREEN_LAYOUTS';
export const CONFIGURATION_SET_EDIT_MODE = 'CONFIGURATION_SET_EDIT_MODE';
export const CONFIGURATION_SET_SPLIT_SCREEN_MODE =
  'CONFIGURATION_SET_SPLIT_SCREEN_MODE';
export const CONFIGURATION_SET_EDIT_MODULE = 'CONFIGURATION_SET_EDIT_MODULE';
export const CONFIGURATION_SET_EDIT_KEY = 'CONFIGURATION_SET_EDIT_KEY';
export const CONFIGURATION_SET_LAYOUT_MANAGER_MODE =
  'CONFIGURATION_SET_LAYOUT_MANAGER_MODE';
export const CONFIGURATION_SET_DASHBOARD_TYPE =
  'CONFIGURATION_SET_DASHBOARD_TYPE';
export const CONFIGURATION_RESET_STATE = 'CONFIGURATION_RESET_STATE';
export const CONFIGURATION_SET_LAST_USED_LAYOUT =
  'CONFIGURATION_SET_LAST_USED_LAYOUT';
export const CONFIGURATION_SET_SHOW_AGENT_CHAT =
  'CONFIGURATION_SET_SHOW_AGENT_CHAT';

export type SetCachedLayoutActionType = {
  type: 'CONFIGURATION_SET_CACHED_LAYOUTS';
  layouts: Dashboard[];
  layout?: Dashboard;
};

export type ClearCachedLayoutActionType = {
  type: 'CONFIGURATION_CLEAR_CACHED_LAYOUTS';
  layout?: Dashboard;
};

export type UpdatedSelectedLayoutActionType = {
  type: 'CONFIGURATION_UPDATE_SELECTED_LAYOUT';
  updatedLayoutConfig: DashboardConfig;
  layout?: Dashboard;
  side?: 'left' | 'right';
};

export type SelectedLayoutSuccesfullyPersistedActionType = {
  type: 'CONFIGURATION_SELECTED_LAYOUT_SUCCESSFULLY_PERSISTED';
  layout?: Dashboard;
};

export type RevertSelectedLayoutChangesActionType = {
  type: 'CONFIGURATION_REVERT_SELECTED_LAYOUT_CHANGES';
  layout?: Dashboard;
};

export type ToggleRawLayoutConfigDialogActionType = {
  type: 'CONFIGURATION_TOGGLE_RAW_LAYOUT_CONFIG_DIALOG';
  isOpen: boolean;
  layout?: Dashboard;
};

export type SetLastUsedLayoutActionType = {
  type: 'CONFIGURATION_SET_LAST_USED_LAYOUT';
  projectId: string;
  dashboardType: DashboardType;
  layoutId: Guid;
  layout?: Dashboard;
};

export type SwitchToLayoutActionType = {
  type: 'CONFIGURATION_SWITCH_LAYOUT';
  layout: Dashboard;
  isStaticDefault: boolean;
} & (
  | {
      view: 'split';
      side: 'left' | 'right';
    }
  | {}
);

export type SetEditModeActionType = {
  type: 'CONFIGURATION_SET_EDIT_MODE';
  isInEditMode: boolean;
  layout?: Dashboard;
};

export type SetSplitScreenModeActionType = {
  type: 'CONFIGURATION_SET_SPLIT_SCREEN_MODE';
  isInSplitScreenMode: boolean;
  layout?: Dashboard;
};

export type SwitchToSplitScreenLayouts = {
  type: 'CONFIGURATION_SWITCH_TO_SPLIT_SCREEN_LAYOUTS';
  layout?: Dashboard;
  leftLayout: Dashboard;
  rightLayout: Dashboard;
};

export type SetEditModuleActionType = {
  type: 'CONFIGURATION_SET_EDIT_MODULE';
  editModuleKey: Guid;
  layout?: Dashboard;
};

export type SetEditSettingsActionType = {
  type: 'CONFIGURATION_SET_EDIT_KEY';
  editSettingsKey: string;
  layout?: Dashboard;
};

export type SetLayoutManagerModeActionType = {
  type: 'CONFIGURATION_SET_LAYOUT_MANAGER_MODE';
  isInLayoutManagerMode: boolean;
  layout?: Dashboard;
};

export type SetDashboardType = {
  type: 'CONFIGURATION_SET_DASHBOARD_TYPE';
  dashboardType: DashboardType;
  layout?: Dashboard;
};

export type ResetStateActionType = {
  type: 'CONFIGURATION_RESET_STATE';
  layout?: Dashboard;
};

export type SetShowAgentChatType = {
  type: 'CONFIGURATION_SET_SHOW_AGENT_CHAT';
  showAgentChat: boolean;
  layout?: Dashboard;
};

export type ActionTypes =
  | SetCachedLayoutActionType
  | ClearCachedLayoutActionType
  | UpdatedSelectedLayoutActionType
  | SelectedLayoutSuccesfullyPersistedActionType
  | RevertSelectedLayoutChangesActionType
  | ToggleRawLayoutConfigDialogActionType
  | SetLastUsedLayoutActionType
  | SwitchToLayoutActionType
  | SetEditModeActionType
  | SetSplitScreenModeActionType
  | SetEditModuleActionType
  | SetEditSettingsActionType
  | SetLayoutManagerModeActionType
  | SetDashboardType
  | ResetStateActionType
  | SwitchToSplitScreenLayouts
  | SetShowAgentChatType;

export const dashboardStateActions = {
  setCachedLayouts: (layouts: Dashboard[]): SetCachedLayoutActionType => ({
    type: CONFIGURATION_SET_CACHED_LAYOUTS,
    layouts,
  }),
  clearCachedLayouts: (): ClearCachedLayoutActionType => ({
    type: CONFIGURATION_CLEAR_CACHED_LAYOUTS,
  }),
  updateSelectedLayout: (
    updatedLayoutConfig: DashboardConfig,
    side?: 'left' | 'right',
  ): UpdatedSelectedLayoutActionType => ({
    type: CONFIGURATION_UPDATE_SELECTED_LAYOUT,
    updatedLayoutConfig,
    side,
  }),
  clearSelectedLayout: (): UpdatedSelectedLayoutActionType => ({
    type: CONFIGURATION_UPDATE_SELECTED_LAYOUT,
    updatedLayoutConfig: emptyObj as DashboardConfig,
  }),
  selectedLayoutSuccesfullyPersisted:
    (): SelectedLayoutSuccesfullyPersistedActionType => ({
      type: CONFIGURATION_SELECTED_LAYOUT_SUCCESSFULLY_PERSISTED,
    }),
  revertSelectedLayoutChanges: (): RevertSelectedLayoutChangesActionType => ({
    type: CONFIGURATION_REVERT_SELECTED_LAYOUT_CHANGES,
  }),
  openRawConfigDialog: (): ToggleRawLayoutConfigDialogActionType => ({
    type: CONFIGURATION_TOGGLE_RAW_LAYOUT_CONFIG_DIALOG,
    isOpen: true,
  }),
  closeRawConfigDialog: (): ToggleRawLayoutConfigDialogActionType => ({
    type: CONFIGURATION_TOGGLE_RAW_LAYOUT_CONFIG_DIALOG,
    isOpen: false,
  }),
  setLastUsedLayout: (
    projectId: string,
    dashboardType: DashboardType,
    layoutId: Guid,
  ): SetLastUsedLayoutActionType => ({
    type: CONFIGURATION_SET_LAST_USED_LAYOUT,
    projectId,
    dashboardType,
    layoutId,
  }),
  switchToLayout: (
    layout: Dashboard,
    isStaticDefault: boolean,
    side?: 'left' | 'right',
  ): SwitchToLayoutActionType => ({
    type: CONFIGURATION_SWITCH_LAYOUT,
    layout,
    isStaticDefault,
    ...(side !== undefined
      ? {
          view: 'split',
          side,
          layoutId: layout.id,
        }
      : {}),
  }),
  switchToSplitScreenLayouts: (
    leftLayout: Dashboard,
    rightLayout: Dashboard,
  ): SwitchToSplitScreenLayouts => ({
    type: CONFIGURATION_SWITCH_TO_SPLIT_SCREEN_LAYOUTS,
    leftLayout,
    rightLayout,
  }),
  setEditMode: (): SetEditModeActionType => ({
    type: CONFIGURATION_SET_EDIT_MODE,
    isInEditMode: true,
  }),
  unsetEditMode: (): SetEditModeActionType => ({
    type: CONFIGURATION_SET_EDIT_MODE,
    isInEditMode: false,
  }),
  setSplitScreenMode: (): SetSplitScreenModeActionType => ({
    type: CONFIGURATION_SET_SPLIT_SCREEN_MODE,
    isInSplitScreenMode: true,
  }),
  unsetSplitScreenMode: (): SetSplitScreenModeActionType => ({
    type: CONFIGURATION_SET_SPLIT_SCREEN_MODE,
    isInSplitScreenMode: false,
  }),
  editModuleSettings: (editModuleKey: Guid): SetEditModuleActionType => ({
    type: CONFIGURATION_SET_EDIT_MODULE,
    editModuleKey,
  }),
  editSettings: (editSettingsKey: string): SetEditSettingsActionType => ({
    type: CONFIGURATION_SET_EDIT_KEY,
    editSettingsKey,
  }),
  doneEditingSettings: (): SetEditSettingsActionType => ({
    type: CONFIGURATION_SET_EDIT_KEY,
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    editSettingsKey: null,
  }),
  doneEditingModuleSettings: (): SetEditModuleActionType => ({
    type: CONFIGURATION_SET_EDIT_MODULE,
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    editModuleKey: null,
  }),
  enterLayoutManagerMode: (): SetLayoutManagerModeActionType => ({
    type: CONFIGURATION_SET_LAYOUT_MANAGER_MODE,
    isInLayoutManagerMode: true,
  }),
  exitLayoutManagerMode: (): SetLayoutManagerModeActionType => ({
    type: CONFIGURATION_SET_LAYOUT_MANAGER_MODE,
    isInLayoutManagerMode: false,
  }),
  setDashboardType: (dashboardType: DashboardType): SetDashboardType => ({
    type: CONFIGURATION_SET_DASHBOARD_TYPE,
    dashboardType,
  }),
  resetState: (): ResetStateActionType => ({
    type: CONFIGURATION_RESET_STATE,
  }),
  setShowAgentChat: (): SetShowAgentChatType => ({
    type: CONFIGURATION_SET_SHOW_AGENT_CHAT,
    showAgentChat: true,
  }),
  unsetShowAgentChat: (): SetShowAgentChatType => ({
    type: CONFIGURATION_SET_SHOW_AGENT_CHAT,
    showAgentChat: false,
  }),
};

export type LayoutConfigurationState = {
  lastUsedLayouts: any;
  dashboardType: DashboardType;
  selectedLayoutConfig: DashboardConfig;
  selectedLayoutsConfig: {
    left: null | DashboardConfig;
    right: null | DashboardConfig;
  };
  persistedVersionOfSelectedConfig: DashboardConfig;
  persistedVersionsOfSelectedConfigs: {
    left: DashboardConfig;
    right: DashboardConfig;
  };
  selectedLayoutId: Guid;
  selectedLayoutsId: {
    left: Guid | null;
    right: Guid | null;
  };
  isRawConfigDialogOpen: boolean;
  isInEditMode: boolean;
  isInSplitScreenMode: boolean;
  isStaticDefaultLayoutSelected: boolean;
  cachedLayouts: Dashboard[];
  editModuleKey: Guid;
  editSettingsKey: string;
  isInLayoutManagerMode: boolean;
  cachedLayoutsInitialized: boolean;
  showAgentChat: boolean;
};

const INITIAL_STATE: LayoutConfigurationState = {
  // maps combination of project and layout type to last selected layout
  lastUsedLayouts: null,
  // represents what dashboard type is currently being used (subject viewer, insights, etc)
  // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
  dashboardType: null,
  // the current displayed/working copy of the config
  // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
  selectedLayoutConfig: null,
  selectedLayoutsConfig: {
    left: null,
    right: null,
  },
  // the current persisted config
  // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
  persistedVersionOfSelectedConfig: null,
  // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
  selectedLayoutId: null,
  isRawConfigDialogOpen: false,
  isInEditMode: false,
  isInSplitScreenMode: false,
  isStaticDefaultLayoutSelected: false,
  cachedLayouts: [],
  // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
  editModuleKey: null,
  // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
  editSettingsKey: null,
  isInLayoutManagerMode: false,
  // if layout cache has been set and initialized
  cachedLayoutsInitialized: false,
  showAgentChat: false,
};

function reducer(
  state: LayoutConfigurationState,
  action: ActionTypes,
): LayoutConfigurationState {
  // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
  const newLayoutId = _get<Dashboard, 'id', Guid>(action?.layout, 'id', null);
  const newLayoutConfig = _get<Dashboard, 'config', DashboardConfig>(
    action?.layout,
    'config',
    {} as DashboardConfig,
  );
  switch (action.type) {
    case CONFIGURATION_SET_DASHBOARD_TYPE:
      return {
        ...state,
        dashboardType: action.dashboardType,
      };
    case CONFIGURATION_SET_CACHED_LAYOUTS:
      return {
        ...state,
        cachedLayouts: action.layouts,
        cachedLayoutsInitialized: true,
      };
    case CONFIGURATION_CLEAR_CACHED_LAYOUTS:
      return {
        ...state,
        cachedLayouts: emptyArray,
      };
    case CONFIGURATION_UPDATE_SELECTED_LAYOUT:
      return {
        ...state,
        ...(state.isInSplitScreenMode && action.side
          ? {
              selectedLayoutsConfig: {
                ...state.selectedLayoutsConfig,
                [action.side]: _cloneDeep(action.updatedLayoutConfig),
              },
            }
          : {
              selectedLayoutConfig: action.updatedLayoutConfig,
            }),
      };
    case CONFIGURATION_REVERT_SELECTED_LAYOUT_CHANGES:
      return {
        ...state,
        selectedLayoutConfig: _cloneDeep(
          state.persistedVersionOfSelectedConfig,
        ),
        ...(state.isInSplitScreenMode
          ? {
              selectedLayoutsConfig: {
                left: _cloneDeep(state.persistedVersionsOfSelectedConfigs.left),
                right: _cloneDeep(
                  state.persistedVersionsOfSelectedConfigs.right,
                ),
              },
            }
          : {}),
      };
    case CONFIGURATION_TOGGLE_RAW_LAYOUT_CONFIG_DIALOG:
      return {
        ...state,
        isRawConfigDialogOpen: action.isOpen,
      };
    case CONFIGURATION_SWITCH_LAYOUT: {
      if (!newLayoutId && !action.isStaticDefault) {
        throw new Error('Cannot switch to a configured layout without an ID');
      }

      const selectedLayoutsConfig = {
        ...state.selectedLayoutsConfig,
      };
      const selectedLayoutsId = {
        ...state.selectedLayoutsId,
      };
      const isSplitView = 'view' in action;
      if (isSplitView) {
        selectedLayoutsConfig[action.side] = _cloneDeep(newLayoutConfig);
        selectedLayoutsId[action.side] = newLayoutId;
      }

      return {
        ...state,
        isStaticDefaultLayoutSelected: !!action.isStaticDefault,

        ...(!isSplitView
          ? {
              selectedLayoutConfig: _cloneDeep(newLayoutConfig),
              persistedVersionOfSelectedConfig: _cloneDeep(newLayoutConfig),
              selectedLayoutId: newLayoutId,
            }
          : {
              selectedLayoutsConfig,
              selectedLayoutsId,
              persistedVersionsOfSelectedConfigs: {
                left:
                  _cloneDeep(state.selectedLayoutsConfig.left) ||
                  _cloneDeep(state.selectedLayoutConfig),
                right:
                  _cloneDeep(state.selectedLayoutsConfig.right) ||
                  _cloneDeep(state.selectedLayoutConfig),
              },
            }),
      };
    }
    case CONFIGURATION_SWITCH_TO_SPLIT_SCREEN_LAYOUTS: {
      return {
        ...state,
        isStaticDefaultLayoutSelected: false,
        isInSplitScreenMode: true,
        selectedLayoutId: action.leftLayout.id,
        persistedVersionOfSelectedConfig: _cloneDeep(action.leftLayout.config),
        persistedVersionsOfSelectedConfigs: {
          left: _cloneDeep(action.leftLayout.config),
          right: _cloneDeep(action.rightLayout.config),
        },
        selectedLayoutConfig: _cloneDeep(action.leftLayout.config),
        selectedLayoutsConfig: {
          left: _cloneDeep(action.leftLayout.config),
          right: _cloneDeep(action.rightLayout.config),
        },
        selectedLayoutsId: {
          left: action.leftLayout.id,
          right: action.rightLayout.id,
        },
      };
    }
    case CONFIGURATION_SELECTED_LAYOUT_SUCCESSFULLY_PERSISTED:
      return {
        ...state,
        persistedVersionOfSelectedConfig: _cloneDeep(
          state.selectedLayoutConfig,
        ),
      };
    case CONFIGURATION_SET_EDIT_MODE:
      return {
        ...state,
        isInEditMode: action.isInEditMode,
      };
    case CONFIGURATION_SET_SPLIT_SCREEN_MODE:
      return {
        ...state,
        isInSplitScreenMode: action.isInSplitScreenMode,
        ...(action.isInSplitScreenMode
          ? {
              selectedLayoutsConfig: {
                left: _cloneDeep(state.selectedLayoutConfig),
                right: _cloneDeep(state.selectedLayoutConfig),
              },
              selectedLayoutsId: {
                left: state.selectedLayoutId,
                right: state.selectedLayoutId,
              },
            }
          : {
              selectedLayoutsConfig: {
                left: null,
                right: null,
              },
              selectedLayoutsId: {
                left: null,
                right: null,
              },
            }),
      };
    case CONFIGURATION_SET_EDIT_MODULE:
      return {
        ...state,
        editModuleKey: action.editModuleKey,
      };
    case CONFIGURATION_SET_EDIT_KEY:
      return {
        ...state,
        editSettingsKey: action.editSettingsKey,
      };
    case CONFIGURATION_SET_LAYOUT_MANAGER_MODE:
      return {
        ...state,
        isInLayoutManagerMode: action.isInLayoutManagerMode,
      };
    case CONFIGURATION_RESET_STATE:
      return {
        ...state,
        ..._cloneDeep(INITIAL_STATE),
      };
    case CONFIGURATION_SET_SHOW_AGENT_CHAT:
      return {
        ...state,
        showAgentChat: action.showAgentChat,
      };
    default:
      return state;
  }
}

export type LayoutConfigDispatch = (action: ActionTypes) => void;

function useLayoutConfigurationState(): [
  LayoutConfigurationState,
  LayoutConfigDispatch,
] {
  return useReducer(reducer, INITIAL_STATE);
}

export default useLayoutConfigurationState;

// selectors
const hasEmptyLayout = (state: LayoutConfigurationState) =>
  _isNil(state.selectedLayoutConfig) || _isEmpty(state.selectedLayoutConfig);

const isEditingModuleSettings = (state: LayoutConfigurationState) =>
  !!state.editModuleKey;

const isEditingNonModuleSettings = (state: LayoutConfigurationState) =>
  !!state.editSettingsKey;

const isEditingSettings = (state: LayoutConfigurationState) =>
  isEditingModuleSettings(state) || isEditingNonModuleSettings(state);

const isLayoutSelected = (state: LayoutConfigurationState) =>
  state.selectedLayoutConfig !== null;

export const layoutConfigSelectors = {
  hasEmptyLayout,
  isEditingModuleSettings,
  isEditingNonModuleSettings,
  isEditingSettings,
  isLayoutSelected,
};
